import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { CategoryTree } from '../types/category';
import {
  HEADER_LOOKUP,
  IMPORT_DATA_SHEET,
  IMPORT_HEADER_ORDER,
  IMPORT_HEADERS,
  ImportRawRow,
  MAX_IMPORT_ROWS,
  SkippedImportRow,
  ValidImportRow,
  isBlankImportRow,
  isExampleImportRow,
  normalizeHeader,
  trimText,
  validateImportRow,
} from './expenseImport';

export type ParsedImportFile = {
  valid: ValidImportRow[];
  skipped: SkippedImportRow[];
  error?: string;
};

const cellText = (cell: ExcelJS.Cell): string => {
  const value = cell.value;
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'object' && value !== null) {
    const rich = value as { text?: string; richText?: Array<{ text: string }>; result?: unknown; hyperlink?: string };
    if (typeof rich.result === 'string' || typeof rich.result === 'number') {
      return String(rich.result);
    }
    if (Array.isArray(rich.richText)) {
      return rich.richText.map((part) => part.text).join('');
    }
    if (typeof rich.text === 'string') return rich.text;
    if (typeof rich.hyperlink === 'string') return rich.hyperlink;
  }
  return String(value);
};

const pickDataSheet = (workbook: ExcelJS.Workbook): ExcelJS.Worksheet | undefined => {
  const named = workbook.getWorksheet(IMPORT_DATA_SHEET);
  if (named) return named;
  return workbook.worksheets.find(
    (sheet) => sheet.state !== 'hidden' && sheet.state !== 'veryHidden' && sheet.name !== '_lists'
  );
};

const COMMENT_PART =
  /(^|\/)(comments\/|comments\d+\.xml|threadedComments|drawings\/(vmlDrawing|commentsDrawing))/i;
const COMMENT_REL =
  /<Relationship\b[^>]*?\bType="[^"]*(?:comments|vmlDrawing|threadedComment)[^"]*"(?:[^>]*\/>|[^>]*>[\s\S]*?<\/Relationship>)/gi;

export const stripXlsxCommentParts = async (buffer: ArrayBuffer): Promise<ArrayBuffer> => {
  const zip = await JSZip.loadAsync(buffer);
  const names = Object.keys(zip.files);
  let changed = false;
  names.forEach((name) => {
    if (COMMENT_PART.test(name)) {
      zip.remove(name);
      changed = true;
    }
  });
  for (const name of names) {
    if (!/worksheets\/_rels\/.+\.rels$/i.test(name)) continue;
    const file = zip.file(name);
    if (!file) continue;
    const xml = await file.async('string');
    const cleaned = xml.replace(COMMENT_REL, '');
    if (cleaned !== xml) {
      zip.file(name, cleaned);
      changed = true;
    }
  }
  if (!changed) return buffer;
  return zip.generateAsync({ type: 'arraybuffer' });
};

const loadImportWorkbook = async (buffer: ArrayBuffer): Promise<ExcelJS.Workbook> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await stripXlsxCommentParts(buffer));
  return workbook;
};

export const parseExpenseImportBuffer = async (
  buffer: ArrayBuffer,
  tree: CategoryTree | null | undefined
): Promise<ParsedImportFile> => {
  let workbook: ExcelJS.Workbook;
  try {
    workbook = await loadImportWorkbook(buffer);
  } catch {
    return { valid: [], skipped: [], error: 'לא ניתן לקרוא את הקובץ. יש להעלות את תבנית האקסל.' };
  }

  const sheet = pickDataSheet(workbook);
  if (!sheet) {
    return { valid: [], skipped: [], error: `חסר גיליון ${IMPORT_DATA_SHEET}` };
  }

  const headerRow = sheet.getRow(1);
  const columnMap: Array<keyof typeof IMPORT_HEADERS | undefined> = [];
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const key = HEADER_LOOKUP[normalizeHeader(cellText(cell))];
    columnMap[colNumber] = key;
  });

  const requiredKeys: Array<keyof typeof IMPORT_HEADERS> = [
    'vendorName',
    'category1',
    'documentDate',
  ];
  const found = new Set(columnMap.filter(Boolean));
  const missing = requiredKeys.filter((key) => !found.has(key));
  if (!found.has('netAmount') && !found.has('totalAmount') && !found.has('vatAmount')) {
    missing.push('totalAmount');
  }
  if (missing.length) {
    return {
      valid: [],
      skipped: [],
      error: `חסרות עמודות חובה: ${missing.map((key) => IMPORT_HEADERS[key]).join(', ')}`,
    };
  }

  const valid: ValidImportRow[] = [];
  const skipped: SkippedImportRow[] = [];
  let dataRows = 0;

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const raw: ImportRawRow = {
      row: rowNumber,
      vendorName: '',
      netAmount: '',
      vatAmount: '',
      totalAmount: '',
      category1: '',
      documentDate: '',
      category2: '',
      category3: '',
      projectTag: '',
      expenseId: '',
      notes: '',
    };
    IMPORT_HEADER_ORDER.forEach((key) => {
      const col = columnMap.findIndex((mapped) => mapped === key);
      if (col > 0) {
        raw[key] = trimText(cellText(row.getCell(col)));
      }
    });
    if (isBlankImportRow(raw) || isExampleImportRow(raw.vendorName)) return;
    dataRows += 1;
    if (dataRows > MAX_IMPORT_ROWS) return;
    const result = validateImportRow(raw, tree);
    if (result.error || !result.valid) {
      skipped.push({ row: rowNumber, reason: result.error || 'שורה לא תקינה', kind: 'invalid' });
      return;
    }
    valid.push(result.valid);
  });

  if (dataRows === 0) {
    return { valid: [], skipped: [], error: 'אין שורות לייבוא בגיליון הוצאות' };
  }
  if (dataRows > MAX_IMPORT_ROWS) {
    return {
      valid: [],
      skipped: [],
      error: `יותר מדי שורות. המקסימום הוא ${MAX_IMPORT_ROWS}`,
    };
  }

  return { valid, skipped };
};

export const parseExpenseImportFile = async (
  file: File,
  tree: CategoryTree | null | undefined
): Promise<ParsedImportFile> => parseExpenseImportBuffer(await file.arrayBuffer(), tree);
